import { useEffect, useRef } from 'react';
import { getAuthToken } from '../utils/authToken';
import { dispatchLiveEvent } from '../utils/liveEventMap';
import { authEvents, responseIsMfaRequired } from '../utils/authEvents';
import logger from '../utils/logger';

/**
 * Cross-channel live updates ("live update" #251).
 *
 * Polls `GET /api/events` while the tab is VISIBLE and re-emits each backend
 * event as the `omnis:*` window event the panels already refetch on — so a
 * change made on Telegram appears in the open Main Office without any
 * refresh, navigation, or click.
 *
 * WHY POLLING, NOT SSE/WEBSOCKETS
 * The backend runs gunicorn with 1 worker / 8 threads. A push connection pins a
 * thread for its entire lifetime, so one stream per open tab would starve the
 * same pool the chat stream needs. Cloud Run also runs several instances with no
 * shared broker, so a connection held on instance A cannot see a mutation
 * committed on instance B. A ~1s poll of one indexed, almost-always-empty query
 * is stateless, instance-agnostic, holds no thread, and lands comfortably inside
 * the ticket's sub-second-feel bar.
 *
 * COST DISCIPLINE (this runs forever, on every session):
 *   - Paused entirely while the tab is hidden; resumes with an IMMEDIATE poll on
 *     focus/visibility so returning to the tab feels instant.
 *   - Zero React state. The hook holds everything in refs and communicates only
 *     via window events, so an empty poll (the normal case) causes NO re-render
 *     anywhere. This is deliberate: a 1s interval that re-rendered the shell
 *     would be the "jitter" class of bug (#206) all over again.
 *   - Errors back off (1s → up to 15s) and reset on the first success, so a
 *     backend blip doesn't turn every open tab into a retry storm.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

// 1s is the sweet spot: the acceptance bar is "under a second" of perceived
// latency, and at this rate the worst case is ~1s of poll wait + one refetch.
// Do not go below this without re-checking backend cost per open tab.
export const LIVE_POLL_MS = 1000;
const MAX_BACKOFF_MS = 15000;

export default function useLiveEvents(enabled = true) {
    // A cursor of `null` means "not bootstrapped yet".
    const cursorRef = useRef(null);
    const timerRef = useRef(null);
    const inFlightRef = useRef(false);
    const backoffRef = useRef(0);
    const stoppedRef = useRef(false);

    useEffect(() => {
        if (!enabled) return undefined;
        stoppedRef.current = false;

        const clearTimer = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };

        const schedule = (delay) => {
            clearTimer();
            if (stoppedRef.current) return;
            timerRef.current = setTimeout(tick, delay);
        };

        async function tick() {
            timerRef.current = null;
            if (stoppedRef.current) return;

            // Skip (don't stop) while hidden or signed out — the visibility
            // handler restarts us, and a missing token just means "not yet".
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            const token = getAuthToken();
            if (!token) { schedule(LIVE_POLL_MS); return; }
            if (inFlightRef.current) { schedule(LIVE_POLL_MS); return; }

            inFlightRef.current = true;
            try {
                // No cursor yet → bootstrap: the server returns only `latest_id`,
                // so we never replay history into a tab that already shows
                // current state.
                const url = cursorRef.current === null
                    ? `${API_BASE_URL}/api/events`
                    : `${API_BASE_URL}/api/events?after=${cursorRef.current}`;

                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) {
                    // 2FA (#333): this ~1s poll is usually the first request a
                    // single-factor session (signed in before 2FA was turned
                    // on) makes — a `mfa_required` 401 signs it out at once.
                    if (res.status === 401 && await responseIsMfaRequired(res)) {
                        authEvents.emitMfaRequired();
                    }
                    throw new Error(`events poll ${res.status}`);
                }
                const body = await res.json();

                for (const event of body.events || []) {
                    dispatchLiveEvent(event);
                }
                if (typeof body.latest_id === 'number') cursorRef.current = body.latest_id;
                backoffRef.current = 0;
            } catch (err) {
                // Back off, but never give up: the app stays fully usable, it
                // just stops live-syncing until the backend answers again.
                backoffRef.current = backoffRef.current
                    ? Math.min(backoffRef.current * 2, MAX_BACKOFF_MS)
                    : LIVE_POLL_MS * 2;
                logger?.debug?.('LIVE', 'events poll failed, backing off', err?.message);
            } finally {
                inFlightRef.current = false;
                schedule(backoffRef.current || LIVE_POLL_MS);
            }
        }

        const onWake = () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            // Returning to the tab should feel instant, and a successful poll
            // clears any accumulated backoff.
            backoffRef.current = 0;
            schedule(0);
        };

        window.addEventListener('focus', onWake);
        document.addEventListener('visibilitychange', onWake);
        schedule(0);

        return () => {
            stoppedRef.current = true;
            clearTimer();
            window.removeEventListener('focus', onWake);
            document.removeEventListener('visibilitychange', onWake);
        };
    }, [enabled]);
}
